import { zodResolver } from "@hookform/resolvers/zod";
import { useEffect, useMemo, useRef, useState } from "react";
import { useForm, useWatch } from "react-hook-form";
import { useSelector } from "react-redux";
import {
  CircleCheck,
  LaptopMinimalCheck,
  LayoutGrid,
  ListChecks,
} from "lucide-react";
import { actionsKeys, getRequiredFields, permissionActions } from "@/lib/utils";
import { RootState } from "@/redux/store";
import { routes } from "@/routes/utils";
import {
  COMMUNITY_NAME,
  DOCUMENT_TYPE,
  FIELD_TABS,
  HEADER_FIELDS,
} from "./consts";
import type { TabValue } from "./types";
import { formValues, schema } from "./validations";
import { useGetGLConfig, useGetDocumentTypeDropdown, useUpdateConfigFieldsMutation } from "@/services";
import { toast } from "@/components/Toast";
import { useQueryClient } from "@tanstack/react-query";

const useGLConfigurationStudio = () => {
  const communityData = useSelector(
    (state: RootState) => state.currentData.communityData
  );
  const accessPermission = useSelector(
    (state: RootState) => state.currentData.accessPermission
  );
  const queryClient = useQueryClient();

  const selectedRowData = useRef<any>(null);
  const [summaryCards, setSummaryCards] = useState(false);
  const [activeTab, setActiveTab] = useState<TabValue>(HEADER_FIELDS);
  const [fieldSearch, setFieldSearch] = useState("");
  const [applicableOnly, setApplicableOnly] = useState(true);
  const [fieldRows, setFieldRows] =
    useState<any>([]);
  const [openConfirmationDialog, setOpenConfirmationDialog] = useState<boolean>(false);
  const [summaryCardList, setSummaryCardList] = useState([
      {
        title: "Total Fields",
        value: 0,
        subtitle: "Header + line-item fields",
        icon: ListChecks,
        key: 'total'
      },
      {
        title: "Applicable Fields",
        value: 0,
        subtitle: "Visible in template / ui",
        icon: CircleCheck,
        key: 'applicable'
      },
      {
        title: "Required Fields",
        value: 0,
        subtitle: "Mandatory at runtime",
        icon: LaptopMinimalCheck,
        key: 'required'
      },
      {
        title: "System / Defaulted",
        value: 0,
        subtitle: "Nonuser-input fields",
        icon: LayoutGrid,
        key: 'defaultCount'
      },
    ]);

  const form = useForm<formValues>({
    resolver: zodResolver(schema),
    defaultValues: {
      [COMMUNITY_NAME]: communityData?.communityName?.toString() || communityData?.name?.toString() || "",
      [DOCUMENT_TYPE]: "",
    },
  });
  
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, []);

  const hasUpdatePermission = useMemo(() => accessPermission?.[actionsKeys?.GL_CONFIG?.toLowerCase()]?.[permissionActions.UPDATE], [accessPermission])
  
  const breadcrumbData = useMemo(
    () => [
      {
        link: `/${routes.cam}/${routes.activityDashboard}`,
        name: `Activity Dashboard - ${communityData?.communityName || communityData?.name || ""}`,
      },
      {
        link: "",
        name: "GL Configuration Studio",
      },
    ],
    [communityData?.communityName]
  );

  const documentTypeId = useWatch({control: form.control, name: DOCUMENT_TYPE});

  useEffect(() => {
    setSummaryCards(false);
  }, [documentTypeId])

  const {data: configData, isLoading: isConfigLoading, refetch: refetchConfigData, isRefetching: isConfigRefetching} = useGetGLConfig({
    communityId: communityData?.communityId,
    documentTypeId: documentTypeId,
    queryParams: {
      enabled: false
    }
  });

  const { mutate: updateFields, isPending: isUpdatePending } =
    useUpdateConfigFieldsMutation({
      onError: (error?: any) => {
        toast.error(
          typeof error?.response?.data?.message === "string"
            ? error?.response?.data?.message
            : "Something went wrong"
        );
        return error?.response?.data?.message;
      },
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: ["gl-config", communityData?.communityId, documentTypeId],
        });
        setOpenConfirmationDialog(false);
        refetchConfigData();
        toast.success("Field Updated Successfully");
      },
    });

  useEffect(() => {
    if (JSON.stringify(configData?.data) != JSON.stringify(fieldRows)) {
      
      const total = configData?.data?.length;
      const applicable = configData?.data?.filter((data: any) => data?.applicable)?.length;
      const required = configData?.data?.filter((data: any) => data?.required)?.length;
      const defaultCount = configData?.data?.filter((data: any) => data?.default)?.length;
      
      const values: any = {total,
        applicable,
        required,
        defaultCount}
        
        const list = summaryCardList?.map((data: any) => {
          return {
            ...data,
            value: values[data?.key],
          }
        })
        setSummaryCardList(list);
        setFieldRows(configData?.data);
    }
  }, [configData]);

  const {data: documentTypes, isLoading: isDocumentTypesLoading} = useGetDocumentTypeDropdown({
    communityId: communityData?.communityId,
    queryParams: {
      enabled: !!communityData?.communityId
    }
  });

  const onSubmit = () => {
    refetchConfigData();
    setSummaryCards(true);
  };

  const rows = useMemo(() => {
    const normalizeSearch = fieldSearch?.toLowerCase();
  
    return configData?.data?.filter((item: any) => {
      const matchesTab = activeTab === item?.fieldLevel;
  
      const matchesApplicable = applicableOnly ? item?.applicable === true : true;
  
      const matchesSearch = item?.displayLabel?.toLowerCase()?.includes(normalizeSearch);
  
      return matchesTab && matchesApplicable && matchesSearch;
    });
  }, [activeTab, applicableOnly, fieldSearch, configData?.data]);


  const onRowChange = (id: string, key: string, value: any) => {
    let row = fieldRows?.find((data: any) => data.ujeFieldMasterId == id);
    selectedRowData.current = {id, key, value, label: row?.displayLabel};
    setOpenConfirmationDialog(true);
  }

  const onConfirmUpdate = () => {
    const {id, key, value} = selectedRowData.current;

    let row = fieldRows?.find((data: any) => data.ujeFieldMasterId == id);

    row = {
      ujeFieldMasterId: row?.ujeFieldMasterId, 
      applicable: row?.applicable,
      required: row?.required,
      readOnly: row?.readOnly || false,
      defaultValue: row?.defaultValue,
      constraints: row?.constraints,
      [key]: value
    }

    updateFields({
      communityId: communityData?.communityId,
      documentTypeId,
      data: {
        fields: [row]
      }
    })
  }

  return {
    activeTab,
    applicableOnly,
    breadcrumbData,
    rows,
    fieldSearch,
    tabs: FIELD_TABS,
    form,
    onRowChange,
    setActiveTab,
    setApplicableOnly,
    setFieldSearch,
    onSubmit,
    requiredFields: getRequiredFields(schema),
    isDocumentTypesLoading,
    summaryCards,
    summaryCardList,
    isConfigLoading,
    isConfigRefetching,
    documentTypes,
    isUpdatePending,
    openConfirmationDialog,
    setOpenConfirmationDialog,
    onConfirmUpdate,
    selectedRowData: selectedRowData.current,
    hasUpdatePermission
  };
};

export default useGLConfigurationStudio;